import React, { useEffect, useState } from 'react';
import { 
  Calculator, 
  FileText, 
  XCircle, 
  Bell, 
  CheckCircle, 
  AlertCircle, 
  DollarSign, 
  Loader2,
  Calendar,
  RefreshCw,
  ArrowLeftRight,
  NotebookPen
} from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { formatDate } from '../../src/lib/utils';
import { ucCodigoKeys } from '../../src/lib/ucCodigo';
import { emissoesDaFatura } from '../../src/lib/timelineCobranca';
import { buscarTitularesDaUc, descreverMotivo, type TitularUc } from '../../src/lib/titularesUc';
import { buscarAlteracoesCadastro, descreverCampo, grupoCampo, juntarAlteracoes, textoSemMotivo } from '../../src/lib/alteracoesCadastro';
import { buscarHistoricoDesconto } from '../../src/lib/descontoHistorico';

interface HistoricoUcTabProps {
  unidadeId: string;
  numeroUc: string;
  outroNumeroUc?: string | null;
}

type EventType = 'calculo' | 'emissao' | 'avulso' | 'cancelamento' | 'lembrete' | 'pagamento' | 'titular' | 'cadastro';

interface TimelineEvent {
  id: string;
  type: EventType;
  date: Date;
  title: string;
  description: string;
  user?: string;
  value?: number;
  monthRef?: string;
  colorClass: string;
  icon: React.ReactNode;
}

const HistoricoUcTab: React.FC<HistoricoUcTabProps> = ({ unidadeId, numeroUc, outroNumeroUc }) => {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!numeroUc) return;
    
    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const timeline: TimelineEvent[] = [];
        
        const ucKeys = ucCodigoKeys(numeroUc, outroNumeroUc);

        const { data: faturas, error: faturasError } = await supabase
          .from('faturas')
          .select('*')
          .in('uc_codigo_norm', ucKeys);
          
        if (faturasError) throw faturasError;
        
        if (faturas) {
          faturas.forEach((fatura: any) => {
            const val = fatura.valor_boleto_vigor || 0;
            const ref = fatura.mes_referencia || '';

            // Cálculos / Criação
            if (fatura.created_at) {
              const isAvulso = fatura.classificacao === 'AVULSO';
              timeline.push({
                id: `calc-${fatura.id}`,
                type: isAvulso ? 'avulso' : 'calculo',
                date: new Date(fatura.created_at),
                title: isAvulso ? 'Cobrança Avulsa Gerada' : 'Cálculo Gerado',
                description: `Referência: ${ref}`,
                value: val,
                monthRef: ref,
                colorClass: isAvulso ? 'bg-indigo-500' : 'bg-gray-500',
                icon: isAvulso ? <DollarSign size={16} className="text-white" /> : <Calculator size={16} className="text-white" />
              });
            }

            // Emissões — TODAS, não só a que está viva.
            //
            // Até 28/08/2026 esta aba lia `emitido_em`/`emitido_por_nome`, que a
            // reemissão sobrescreve: a UC que teve o boleto cancelado e refeito
            // mostrava um único evento, com o nome de quem mexeu por último.
            // `emissoesDaFatura` cai naqueles dois campos quando a fatura é antiga
            // demais para ter histórico, então nada some.
            if (fatura.status_fatura !== 'Cancelada' && fatura.status_fatura !== 'Excluído') {
              emissoesDaFatura(fatura).forEach((emissao, idx, todas) => {
                const reemissao = idx > 0;
                timeline.push({
                  id: `emissao-${fatura.id}-${idx}`,
                  type: 'emissao',
                  date: new Date(emissao.emitido_em as string),
                  title: reemissao ? `Boleto Reemitido (${idx + 1}ª de ${todas.length})` : 'Boleto Emitido',
                  description: `Emitido por: ${emissao.emitido_por_nome || 'Sistema'}. Referência: ${ref}`,
                  user: emissao.emitido_por_nome || undefined,
                  value: val,
                  monthRef: ref,
                  colorClass: reemissao ? 'bg-amber-500' : 'bg-blue-500',
                  icon: reemissao ? <RefreshCw size={16} className="text-white" /> : <FileText size={16} className="text-white" />
                });
              });
            }

            // Pagamento
            if (fatura.status_boleto_vigor === 'Pago') {
              const payDate = fatura.atualizado_em || fatura.vencimento_boleto || fatura.created_at;
              timeline.push({
                id: `pag-${fatura.id}`,
                type: 'pagamento',
                date: new Date(payDate),
                title: 'Pagamento Confirmado',
                description: `Boleto ref. ${ref} consta como pago.`,
                value: val,
                monthRef: ref,
                colorClass: 'bg-emerald-500',
                icon: <CheckCircle size={16} className="text-white" />
              });
            }

            // Cancelamento / Exclusão (Status atual ou Histórico)
            if (fatura.status_fatura === 'Excluído' || fatura.status_fatura === 'Cancelada') {
              const cancelDate = fatura.cancelado_em || fatura.updated_at || fatura.created_at;
              timeline.push({
                id: `canc-${fatura.id}`,
                type: 'cancelamento',
                date: new Date(cancelDate),
                title: 'Fatura Cancelada/Excluída',
                description: `Referência: ${ref}. Motivo: ${fatura.motivo_cancelamento_boleto || 'Não informado'}`,
                user: fatura.cancelado_por_nome,
                value: val,
                monthRef: ref,
                colorClass: 'bg-red-500',
                icon: <XCircle size={16} className="text-white" />
              });
            }

            // Se houver histórico de cancelamentos no json
            if (Array.isArray(fatura.historico_cancelamentos)) {
              fatura.historico_cancelamentos.forEach((hist: any, idx: number) => {
                if (hist.cancelado_em) {
                  timeline.push({
                    id: `hist-canc-${fatura.id}-${idx}`,
                    type: 'cancelamento',
                    date: new Date(hist.cancelado_em),
                    title: 'Fatura Cancelada',
                    description: `Referência: ${ref}. Motivo: ${hist.motivo_cancelamento_boleto || 'Não informado'}`,
                    user: hist.cancelado_por_nome,
                    value: val,
                    monthRef: ref,
                    colorClass: 'bg-red-500',
                    icon: <XCircle size={16} className="text-white" />
                  });
                }
              });
            }
          });
        }

        // 2. Fetch Lembretes
        const { data: lembretes, error: lembretesError } = await supabase
          .from('lembrete_cobranca_log')
          .select('*')
          .in('uc_codigo_norm', ucKeys);
          
        if (lembretesError) throw lembretesError;
        
        if (lembretes) {
          lembretes.forEach((lembrete: any) => {
            if (lembrete.enviado_em) {
              const success = lembrete.status === 'enviado' || lembrete.status === 'reenviado';
              timeline.push({
                id: `lemb-${lembrete.id}`,
                type: 'lembrete',
                date: new Date(lembrete.enviado_em),
                title: `Aviso de Vencimento (${lembrete.tipo_lembrete})`,
                description: success 
                  ? `Lembrete enviado via WhatsApp para ${lembrete.whatsapp_destino}` 
                  : `Falha ao enviar lembrete para ${lembrete.whatsapp_destino}: ${lembrete.error_details || ''}`,
                value: lembrete.valor_boleto,
                monthRef: lembrete.mes_referencia,
                colorClass: success ? 'bg-teal-500' : 'bg-orange-500',
                icon: success ? <Bell size={16} className="text-white" /> : <AlertCircle size={16} className="text-white" />
              });
            }
            // Reenvios
            if (lembrete.reenviado_em && lembrete.status === 'reenviado') {
              timeline.push({
                id: `reenv-${lembrete.id}`,
                type: 'lembrete',
                date: new Date(lembrete.reenviado_em),
                title: `Reenvio de Aviso (${lembrete.tipo_lembrete})`,
                description: `Reenviado por ${lembrete.reenviado_por_nome || 'Usuário'} para ${lembrete.whatsapp_destino}`,
                user: lembrete.reenviado_por_nome,
                value: lembrete.valor_boleto,
                monthRef: lembrete.mes_referencia,
                colorClass: 'bg-teal-500',
                icon: <Bell size={16} className="text-white" />
              });
            }
          });
        }

        // 3. Trocas de titular (#6238). Quem já estava no cadastro quando o histórico
        // começou e o que foi registrado à mão ficam só no quadro de titulares, acima.
        // Falha aqui (papel sem acesso, por exemplo) não derruba o resto da linha do tempo.
        const titulares: TitularUc[] = await buscarTitularesDaUc(supabase, Number(unidadeId)).catch(() => []);
        titulares.forEach(t => {
          if (t.motivo === 'carga_inicial' || t.motivo === 'registro_manual') return;
          const cliente = t.papel === 'cliente';
          if (t.motivo === 'cadastro' && !cliente) return;
          const anterior = titulares
            .filter(o => o.papel === t.papel && o.fim && new Date(o.fim).getTime() <= new Date(t.inicio).getTime())
            .sort((a, b) => new Date(b.fim as string).getTime() - new Date(a.fim as string).getTime())[0];
          const nome = t.nome || 'sem nome no cadastro';
          timeline.push({
            id: `titular-${t.id}`,
            type: 'titular',
            date: new Date(t.inicio),
            title: t.motivo === 'cadastro'
              ? 'UC cadastrada'
              : cliente ? 'Cliente da Vigor trocado' : 'Titular da conta de luz trocado',
            description: t.motivo === 'cadastro'
              ? `Cliente: ${nome}.`
              : `${anterior?.nome ? `De ${anterior.nome} para ${nome}` : `Passou a constar: ${nome}`}. ${descreverMotivo(t)}.`,
            user: t.registrado_por_nome && t.registrado_por_nome !== 'Sistema' ? t.registrado_por_nome : undefined,
            colorClass: 'bg-purple-500',
            icon: <ArrowLeftRight size={16} className="text-white" />
          });
        });

        // 4. Mudanças no cadastro e no desconto, com quem, quando e o motivo
        // (uc_alteracoes_cadastro, desde 29/10/2026, e uc_desconto_historico, desde 09/10/2026).
        // Falha aqui também não derruba o resto.
        const [regCadastro, regDesconto] = await Promise.all([
          buscarAlteracoesCadastro(supabase, unidadeId).catch(() => []),
          buscarHistoricoDesconto(supabase, unidadeId).catch(() => []),
        ]);
        juntarAlteracoes(regCadastro, regDesconto).forEach(a => {
          const soDesconto = a.campos.every(c => grupoCampo(c.campo) === 'desconto');
          timeline.push({
            id: `cadastro-${a.chave}`,
            type: 'cadastro',
            date: new Date(a.alteradoEm),
            title: a.soNaFatura ? 'Desconto só numa fatura' : soDesconto ? 'Desconto alterado' : 'Cadastro alterado',
            description: `${a.campos.map(descreverCampo).join('; ')}. ${a.motivo ? `Motivo: “${a.motivo}”` : textoSemMotivo(a)}${a.onde ? ` (${a.onde})` : ''}.`,
            user: a.alteradoPor || undefined,
            colorClass: 'bg-blue-500',
            icon: <NotebookPen size={16} className="text-white" />
          });
        });

        // Sort descending
        timeline.sort((a, b) => b.date.getTime() - a.date.getTime());
        setEvents(timeline);
        
      } catch (err: any) {
        console.error('Erro ao buscar histórico da UC:', err);
        setError(err.message || 'Erro ao carregar o histórico');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [numeroUc, unidadeId]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-gray-500 space-y-4">
        <Loader2 size={32} className="animate-spin text-primary" />
        <p className="text-sm">Buscando o histórico completo da UC...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-12 text-center text-red-500">
        <AlertCircle size={48} className="mx-auto mb-4 opacity-50" />
        <p className="font-medium">Ocorreu um erro ao carregar o histórico.</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="p-12 text-center text-gray-500 flex flex-col items-center">
        <Calendar size={48} className="mx-auto mb-4 text-gray-300 dark:text-gray-600" />
        <p className="font-medium text-lg text-gray-900 dark:text-white">Nenhum evento registrado</p>
        <p className="text-sm mt-2 max-w-md">Esta unidade consumidora ainda não possui cálculos, emissões, pagamentos ou lembretes no seu histórico.</p>
      </div>
    );
  }

  const formatBRL = (val?: number) => {
    if (val === undefined || val === null) return null;
    return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  return (
    <div className="p-6 md:p-8 animate-fade-in bg-gray-50 dark:bg-gray-900/20 min-h-[400px]">
      <div className="max-w-3xl mx-auto">
        <div className="relative border-l-2 border-gray-200 dark:border-gray-800 ml-4 md:ml-6 pb-4">
          
          {events.map((evt, index) => (
            <div key={evt.id} className="mb-8 relative pl-8 md:pl-10 last:mb-0 group">
              {/* Timeline Connector/Icon */}
              <div className={`absolute -left-[17px] top-1 h-8 w-8 rounded-full border-4 border-gray-50 dark:border-gray-900 ${evt.colorClass} flex items-center justify-center shadow-sm z-10 transition-transform group-hover:scale-110`}>
                {evt.icon}
              </div>

              {/* Event Card */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 md:p-5 shadow-sm border border-gray-100 dark:border-gray-700/60 transition-all hover:shadow-md hover:border-gray-200 dark:hover:border-gray-600">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-2 mb-2">
                  <div>
                    <h4 className="text-[15px] font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      {evt.title}
                      {evt.monthRef && (
                        <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-[10px] uppercase font-bold tracking-wider">
                          Ref: {evt.monthRef}
                        </span>
                      )}
                    </h4>
                  </div>
                  <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded-md shrink-0 border border-gray-100 dark:border-gray-700">
                    {formatDate(evt.date.toISOString())}
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {evt.description}
                </p>

                {/* Footer metadata */}
                {(evt.value !== undefined || evt.user) && (
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex flex-wrap gap-4 items-center">
                    {evt.value !== undefined && evt.value > 0 && (
                      <div className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-1">
                        <span className="text-gray-400 font-normal text-xs uppercase">Valor:</span>
                        {formatBRL(evt.value)}
                      </div>
                    )}
                    {evt.user && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600"></span>
                        Por: <span className="font-medium text-gray-700 dark:text-gray-300">{evt.user}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          
        </div>
      </div>
    </div>
  );
};

export default HistoricoUcTab;
